import React, { useState } from 'react';
import {
  IconButton, Tooltip, Menu, MenuItem, Typography, Box,
  Divider, CircularProgress
} from '@mui/material';
import { StarOutlined, StarBorderOutlined, OpenInNewOutlined, CloseOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useFavourites } from '../../hooks/useFavourites';

const getFavouriteDisplayName = (reference: string, name: string) =>
  reference === '/knowledge/ai-creator' ? 'AI Builder' : name;

const FavouritesMenu: React.FC = () => {
  const navigate = useNavigate();
  const { favourites, loading, removeFavourite } = useFavourites();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleNavigate = (reference: string) => {
    handleClose();
    navigate(reference);
  };

  const handleRemove = (e: React.MouseEvent<HTMLElement>, favouriteType: string, favouriteReference: string) => {
    e.stopPropagation();
    removeFavourite(favouriteType, favouriteReference);
  };

  return (
    <>
      <Tooltip title="Favourites">
        <IconButton
          size="small"
          onClick={handleOpen}
          sx={{
            color: anchorEl ? 'primary.main' : 'text.secondary',
            borderRadius: '4px',
            '&:hover': { color: 'primary.main', bgcolor: theme => theme.palette.mode === 'dark' ? '#13243a' : '#F5F5F5' },
          }}
        >
          {favourites.length > 0
            ? <StarOutlined sx={{ fontSize: 20, color: '#f59e0b' }} />
            : <StarBorderOutlined sx={{ fontSize: 20 }} />
          }
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 240,
            maxWidth: 320,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            borderRadius: '4px',
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
            Favourites
          </Typography>
        </Box>
        <Divider sx={{ borderColor: 'divider' }} />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : favourites.length === 0 ? (
          <Box sx={{ px: 2, py: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
              No favourites yet. Click the star icon on any page to add it here.
            </Typography>
          </Box>
        ) : (
          favourites.map((fav) => (
            <MenuItem
              key={fav.favouriteId}
              onClick={() => handleNavigate(fav.favouriteReference)}
              sx={{
                gap: 1.5,
                py: 0.875,
                px: 2,
                color: '#111111',
                '&:hover': { bgcolor: '#F5F5F5' },
              }}
            >
              <StarOutlined sx={{ fontSize: 16, color: '#f59e0b', flexShrink: 0 }} />
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.8125rem',
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {getFavouriteDisplayName(fav.favouriteReference, fav.favouriteName)}
              </Typography>
              <Tooltip title="Open">
                <OpenInNewOutlined sx={{ fontSize: 14, color: '#9E9E9E', flexShrink: 0 }} />
              </Tooltip>
              <Tooltip title="Remove from favourites">
                <IconButton
                  size="small"
                  onClick={(e) => handleRemove(e, fav.favouriteType, fav.favouriteReference)}
                  sx={{ color: '#9E9E9E', p: 0.25 }}
                >
                  <CloseOutlined sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
};

export default FavouritesMenu;
